package com.siriusxm.pia.components

import androidx.compose.runtime.*
import org.jetbrains.compose.web.css.AlignItems
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.alignItems
import org.jetbrains.compose.web.css.display
import org.jetbrains.compose.web.css.paddingLeft
import org.jetbrains.compose.web.css.paddingRight
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div


interface ExpandingTableConfig<T> {
    var items: List<T>
    fun collapsedContent(block: @Composable (T) -> Unit)
    fun expandedContent(block: @Composable (T) -> Unit)

    /**
     * If true, renders the collapsed content even when expanded. The expanded
     * callback will then be called.
     */
    var alwaysShowCollapsed: Boolean

    /**
     * Set of items that should be initially expanded when the table is first rendered.
     */
    var initialExpanded: Set<T>
}

/**
 * Controller that can broadcast expand/collapse commands to every registered
 * expanding table instance. Tables register automatically when a controller is
 * supplied to [expandingRowTable].
 */
class ExpandingTableController {
    private data class Handle(
        val expandAll: () -> Unit,
        val collapseAll: () -> Unit
    )

    private val handles = mutableSetOf<Handle>()
    private enum class Command { NONE, EXPAND, COLLAPSE }
    private var lastCommand: Command = Command.NONE

    internal fun register(onExpandAll: () -> Unit, onCollapseAll: () -> Unit): () -> Unit {
        val handle = Handle(onExpandAll, onCollapseAll)
        handles += handle
        when (lastCommand) {
            Command.EXPAND -> handle.expandAll()
            Command.COLLAPSE -> handle.collapseAll()
            Command.NONE -> {}
        }
        return { handles -= handle }
    }

    fun expandAll() {
        lastCommand = Command.EXPAND
        handles.toList().forEach { it.expandAll() }
    }

    fun collapseAll() {
        lastCommand = Command.COLLAPSE
        handles.toList().forEach { it.collapseAll() }
    }
}

@Composable
fun <T> expandingRowTable(
    controller: ExpandingTableController? = null,
    block: ExpandingTableConfig<T>.() -> Unit
) {
    var collapsed: @Composable (T) -> Unit = { }
    var expandedRender: @Composable (T) -> Unit = { }
    var tableItems: List<T>? = null
    var alwaysShowCollapsed = false
    var initialExpandedItems: Set<T> = emptySet()

    object : ExpandingTableConfig<T> {
        override var items: List<T>
            get() = tableItems.orEmpty()
            set(value) {
                tableItems = value
            }

        override fun collapsedContent(block: @Composable (T) -> Unit) {
            collapsed = block
        }

        override fun expandedContent(block: @Composable (T) -> Unit) {
            expandedRender = block
        }

        override var alwaysShowCollapsed: Boolean
            get() = alwaysShowCollapsed
            set(value) {
                alwaysShowCollapsed = value
            }

        override var initialExpanded: Set<T>
            get() = initialExpandedItems
            set(value) {
                initialExpandedItems = value
            }
    }.block()

    val expandedState = remember(initialExpandedItems) { mutableStateOf(initialExpandedItems) }
    var expanded by expandedState
    val itemsRef = remember { mutableStateOf<List<T>>(emptyList()) }
    itemsRef.value = tableItems.orEmpty()

    if (controller != null) {
        DisposableEffect(controller) {
            val unregister = controller.register(
                onExpandAll = { expandedState.value = itemsRef.value.toSet() },
                onCollapseAll = { expandedState.value = emptySet() }
            )
            onDispose { unregister() }
        }
    }

    table<T> {
        items(tableItems)
        column {
            width = 25.px
            style {
                property("vertical-align", "top")
                paddingRight(0.px)
                paddingLeft(5.px)
            }
            content { item ->
                val isExpanded = expanded.contains(item)
                // Center the chevron within a box whose height matches the content
                // column's first text line (TableStyles td line-height, 1.2rem). Without
                // this, the tight 14px icon box top-aligns against the taller text line
                // and the chevron sits visibly out of line with the row's first line.
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        alignItems(AlignItems.Center)
                        property("line-height", "1.2rem")
                        property("height", "1.2rem")
                    }
                }) {
                    expandingIcon(isExpanded) {
                        onToggle {
                            expanded = if (isExpanded) expanded - item else expanded + item
                        }
                    }
                }
            }
        }
        column {
            style {
                property("vertical-align", "top")
                paddingLeft(0.px)
            }
            content { item ->
                if (expanded.contains(item)) {
                    if (alwaysShowCollapsed) {
                        collapsed(item)
                    }
                    expandedRender(item)
                } else {
                    collapsed(item)
                }
            }
        }
    }
}
